// src/context/ThemeContext.jsx - Light Theme Only Version
import React, { createContext, useContext, useState, useEffect } from "react";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Always set to light theme (false = light)
  const [isDarkMode] = useState(false);

  useEffect(() => {
    // Force light theme on initialization
    const root = document.documentElement;

    // Remove any existing theme classes
    root.classList.remove("dark", "light");

    // Always add light class
    root.classList.add("light");

    // Force update CSS variables for light theme
    root.style.setProperty("--color-bg-primary", "#ffffff");
    root.style.setProperty("--color-bg-secondary", "#f8f9fa");
    root.style.setProperty("--color-bg-tertiary", "#f1f3f4");
    root.style.setProperty("--color-text-primary", "#212529");
    root.style.setProperty("--color-text-secondary", "#6c757d");
    root.style.setProperty("--color-text-tertiary", "#9ca3af");
    root.style.setProperty("--color-border-color", "#dee2e6");
    root.style.setProperty("--color-border-light", "#f1f3f4");
    root.style.setProperty("--color-accent", "#10b981");
    root.style.setProperty("--color-accent-hover", "#059669");
    root.style.setProperty("--color-accent-light", "#d1fae5");

    // Status colors for light theme
    root.style.setProperty("--color-success", "#10b981");
    root.style.setProperty("--color-success-bg", "#dcfce7");
    root.style.setProperty("--color-success-border", "#bbf7d0");
    root.style.setProperty("--color-error", "#ef4444");
    root.style.setProperty("--color-error-bg", "#fef2f2");
    root.style.setProperty("--color-error-border", "#fecaca");
    root.style.setProperty("--color-warning", "#f59e0b");
    root.style.setProperty("--color-warning-bg", "#fef3c7");
    root.style.setProperty("--color-warning-border", "#fde68a");
    root.style.setProperty("--color-info", "#10b981");
    root.style.setProperty("--color-info-bg", "#d1fae5");
    root.style.setProperty("--color-info-border", "#bbf7d0");

    // Light shadows
    root.style.setProperty(
      "--shadow",
      "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
    );
    root.style.setProperty(
      "--shadow-md",
      "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    );
    root.style.setProperty(
      "--shadow-lg",
      "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    );
    root.style.setProperty(
      "--shadow-xl",
      "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
    );


    // Clear any stored theme preferences to prevent conflicts
    localStorage.removeItem("theme");
    localStorage.removeItem("app-theme");

    // Force a re-render of components that might need theme updates
    const themeEvent = new CustomEvent("themeChange", {
      detail: { isDarkMode: false, theme: "light" },
    });
    window.dispatchEvent(themeEvent);
  }, []);

  // Disabled theme toggle function - no longer functional
  const toggleTheme = () => {
    console.log(
      "🔒 Theme toggle disabled - customer app uses light theme only",
    );
    // Do nothing - theme toggle is disabled
  };

  // Get current theme info for debugging (always light)
  const getThemeInfo = () => {
    const root = document.documentElement;
    return {
      isDarkMode: false,
      theme: "light",
      rootClasses: root.className,
      localStorage: "light",
      systemPreference: "light",
      cssVariables: {
        bgPrimary:
          getComputedStyle(root).getPropertyValue("--color-bg-primary"),
        textPrimary: getComputedStyle(root).getPropertyValue(
          "--color-text-primary",
        ),
        borderColor: getComputedStyle(root).getPropertyValue(
          "--color-border-color",
        ),
        accent: getComputedStyle(root).getPropertyValue("--color-accent"),
      },
    };
  };

  // Helper function to apply light theme styles
  const getThemeStyles = (lightStyles) => {
    // Always return light styles
    return lightStyles;
  };

  // Helper function for theme-aware colors (always light theme colors)
  const getThemeColor = (colorKey) => {
    const root = document.documentElement;
    const computedStyles = getComputedStyle(root);
    return computedStyles.getPropertyValue(`--color-${colorKey}`).trim();
  };

  const value = {
    isDarkMode: false, // Always false
    toggleTheme, // Disabled function
    getThemeInfo,
    getThemeStyles,
    getThemeColor,
    theme: "light", // Always light
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
